(() => {
  'use strict';

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const effectiveType = connection && connection.effectiveType ? connection.effectiveType : '';
  const constrainedConnection = Boolean(
    (connection && connection.saveData) || /(slow-2g|2g|3g)/.test(effectiveType)
  );
  const documentRequests = new Map();
  const imageRequests = new Set();
  const activeImages = new Set();
  const MAX_DOCUMENTS = 48;
  const MAX_COLLECTION_IMAGES = 4;
  const MAX_IDLE_VENDOR_FILTERS = 3;
  let hoverIntentTimer = 0;
  let hoverIntentLink = null;

  function runWhenIdle(callback, timeout = 1800) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout });
    } else {
      window.setTimeout(callback, 80);
    }
  }

  function isUsableLink(link) {
    if (!link || !link.href || link.hasAttribute('download')) return false;
    if (link.matches('[data-disabled], [aria-disabled="true"]')) return false;
    if (link.target && link.target.toLowerCase() !== '_self') return false;

    const url = new URL(link.href, window.location.href);
    return url.origin === window.location.origin && /^https?:$/.test(url.protocol);
  }

  function isProductOrCollectionLink(link) {
    if (!isUsableLink(link)) return false;
    if (link.matches('[data-filter-input], [data-product-page-link]')) return true;

    const url = new URL(link.href, window.location.href);
    return /\/(products|collections)\//.test(url.pathname);
  }

  function normalizedUrl(link) {
    const url = new URL(link.href, window.location.href);
    url.hash = '';

    // Empire preserves these display choices while composing filter URLs in
    // JavaScript. Mirroring them here makes the prefetched URL the exact URL
    // that the browser will open after a filter click.
    if (link.matches('[data-filter-input]')) {
      const current = new URL(window.location.href);
      ['sort_by', 'grid_list', 'view'].forEach((parameter) => {
        if (current.searchParams.has(parameter) && !url.searchParams.has(parameter)) {
          url.searchParams.set(parameter, current.searchParams.get(parameter));
        }
      });
    }

    return url.href;
  }

  function warmImage(source, sourceSet, sizes, priority = 'low') {
    if (!source || source.startsWith('data:')) return;

    const key = sourceSet && !sourceSet.startsWith('data:') ? sourceSet : source;
    if (imageRequests.has(key)) return;
    imageRequests.add(key);

    const image = new Image();
    activeImages.add(image);
    image.decoding = 'async';
    if ('fetchPriority' in image) image.fetchPriority = priority;
    if (sizes) image.sizes = sizes;
    if (sourceSet && !sourceSet.startsWith('data:')) image.srcset = sourceSet;
    image.src = source;

    const release = () => activeImages.delete(image);
    image.addEventListener('load', release, { once: true });
    image.addEventListener('error', release, { once: true });
  }

  function absoluteUrl(value, baseUrl) {
    if (!value) return '';
    try {
      return new URL(value, baseUrl).href;
    } catch (error) {
      return '';
    }
  }

  function warmProductImage(parsedDocument, baseUrl) {
    const selectedImage = parsedDocument.querySelector(
      '[data-gallery-selected="true"] .product-gallery--loaded-image'
    );
    if (selectedImage) {
      warmImage(
        absoluteUrl(selectedImage.getAttribute('src'), baseUrl),
        selectedImage.getAttribute('srcset') || '',
        selectedImage.getAttribute('sizes') || '',
        'high'
      );
      return;
    }

    const preloads = Array.from(parsedDocument.querySelectorAll('link[rel="preload"][as="image"]'));
    const preload = preloads.find((item) => item.getAttribute('fetchpriority') === 'high') || preloads[0];
    if (!preload) return;

    warmImage(
      absoluteUrl(preload.getAttribute('href'), baseUrl),
      preload.getAttribute('imagesrcset') || '',
      preload.getAttribute('imagesizes') || '',
      'high'
    );
  }

  function warmCollectionImages(parsedDocument, baseUrl) {
    const images = Array.from(
      parsedDocument.querySelectorAll('.productgrid--items .productitem--image-primary')
    ).slice(0, MAX_COLLECTION_IMAGES);

    images.forEach((item) => {
      warmImage(
        absoluteUrl(item.getAttribute('src'), baseUrl),
        item.getAttribute('srcset') || '',
        item.getAttribute('sizes') || '',
        'low'
      );
    });
  }

  function prefetchDocument(link) {
    if (!isProductOrCollectionLink(link)) return Promise.resolve();

    const url = normalizedUrl(link);
    if (url === window.location.href) return Promise.resolve();
    if (documentRequests.has(url)) return documentRequests.get(url);

    // Bound only the JavaScript bookkeeping. Completed responses remain in
    // the browser HTTP cache and older destinations may still be warmed again.
    if (documentRequests.size >= MAX_DOCUMENTS) {
      documentRequests.delete(documentRequests.keys().next().value);
    }

    const request = window.fetch(url, {
      credentials: 'same-origin',
      redirect: 'follow'
    }).then((response) => {
      const contentType = response.headers.get('content-type') || '';
      if (!response.ok || !contentType.includes('text/html')) return '';
      return response.text();
    }).then((html) => {
      if (!html) return;
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const destination = new URL(url);

      if (/\/products\//.test(destination.pathname)) {
        warmProductImage(parsedDocument, url);
      } else {
        warmCollectionImages(parsedDocument, url);
      }
    }).catch(() => {
      documentRequests.delete(url);
    });

    documentRequests.set(url, request);
    return request;
  }

  function intendedLink(target) {
    if (!(target instanceof Element)) return null;

    const directLink = target.closest(
      'a[data-filter-input], a[data-product-page-link], a[href*="/products/"], a[href*="/collections/"]'
    );
    if (directLink) return directLink;

    const productCard = target.closest('[data-product-item]');
    return productCard ? productCard.querySelector('a[data-product-page-link]') : null;
  }

  function markManagedLinks(root = document) {
    if (!root.querySelectorAll) return;
    root.querySelectorAll(
      'a[data-filter-input], a[data-product-page-link], a[href*="/products/"], a[href*="/collections/"]'
    ).forEach((link) => {
      // Prevent instantPage from issuing a second request for destinations
      // handled here. This prefetcher also warms the next page's real images.
      link.dataset.noInstant = '';
    });
  }

  function handleIntent(event) {
    const link = intendedLink(event.target);
    if (!link) return;
    link.dataset.noInstant = '';

    if (event.type === 'pointerover' && event.relatedTarget instanceof Node && link.contains(event.relatedTarget)) {
      return;
    }

    if (event.type === 'pointerover') {
      window.clearTimeout(hoverIntentTimer);
      hoverIntentLink = link;
      hoverIntentTimer = window.setTimeout(() => {
        prefetchDocument(link);
        hoverIntentLink = null;
      }, 40);
      return;
    }

    prefetchDocument(link);
  }

  function cancelHoverIntent(event) {
    if (!hoverIntentLink) return;
    const leavingLink = intendedLink(event.target);
    const enteringLink = intendedLink(event.relatedTarget);
    if (leavingLink !== hoverIntentLink || enteringLink === hoverIntentLink) return;

    window.clearTimeout(hoverIntentTimer);
    hoverIntentLink = null;
  }

  function vendorFilterLinks() {
    const preferredBrands = ['milwaukee', 'wurth', 'würth', 'hilti'];
    return Array.from(document.querySelectorAll('a[data-filter-input]')).filter((link) => {
      const name = (link.dataset.name || '').toLowerCase();
      return name.includes('vendor') && !link.matches('[data-disabled], [aria-disabled="true"]');
    }).sort((first, second) => {
      const firstValue = (first.dataset.value || first.textContent || '').trim().toLowerCase();
      const secondValue = (second.dataset.value || second.textContent || '').trim().toLowerCase();
      const firstRank = preferredBrands.indexOf(firstValue);
      const secondRank = preferredBrands.indexOf(secondValue);
      return (firstRank < 0 ? preferredBrands.length : firstRank)
        - (secondRank < 0 ? preferredBrands.length : secondRank);
    });
  }

  function warmLikelyVendorFilters() {
    if (constrainedConnection || document.visibilityState === 'hidden') return;

    const uniqueLinks = [];
    const urls = new Set();
    vendorFilterLinks().forEach((link) => {
      if (uniqueLinks.length >= MAX_IDLE_VENDOR_FILTERS) return;
      const url = normalizedUrl(link);
      if (urls.has(url)) return;
      urls.add(url);
      uniqueLinks.push(link);
    });

    function warmNext(index) {
      if (index >= uniqueLinks.length) return;
      runWhenIdle(() => {
        prefetchDocument(uniqueLinks[index]).finally(() => warmNext(index + 1));
      }, 2500);
    }

    warmNext(0);
  }

  document.addEventListener('pointerover', handleIntent, { capture: true, passive: true });
  document.addEventListener('pointerout', cancelHoverIntent, { capture: true, passive: true });
  document.addEventListener('pointerdown', handleIntent, { capture: true, passive: true });
  document.addEventListener('touchstart', handleIntent, { capture: true, passive: true });
  document.addEventListener('focusin', handleIntent, true);

  markManagedLinks(document);

  if (document.readyState === 'complete') {
    runWhenIdle(warmLikelyVendorFilters);
  } else {
    window.addEventListener('load', () => runWhenIdle(warmLikelyVendorFilters), { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => {
    markManagedLinks(event.target);
    runWhenIdle(warmLikelyVendorFilters);
  });
})();
